import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Calendar } from "lucide-react";
import { api, openBooking } from "../lib/site";
import PageHero from "../components/site/PageHero";
import { Overline } from "../components/site/Primitives";

const EASE = [0.16, 1, 0.3, 1];

const INTERESTS = [
  "27-Hour Nail Trainee Course",
  "Nail Boss Blueprint / Coaching",
  "Luxury Foot Care & Services",
  "Products",
  "General Inquiry",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: INTERESTS[0], message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thank you! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", interest: INTERESTS[0], message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-brand-ink/15 bg-white px-5 py-3.5 text-brand-ink placeholder:text-brand-ink/40 outline-none focus:border-brand-gold transition-colors";

  return (
    <div data-testid="contact-page">
      <PageHero overline="Get in Touch" title="Let's start something beautiful." subtitle="Questions about training, coaching, services, or products? Send us a note or book directly — we'd love to hear from you." />

      <section className="bg-brand-cream py-24 md:py-32" data-testid="contact-body">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_1.2fr] gap-14">
          <div>
            <Overline className="mb-6">Reach us</Overline>
            <div className="space-y-6">
              {[
                { icon: Mail, label: "Email", value: "hello@nervanibeauty.com" },
                { icon: Phone, label: "Phone", value: "Placeholder — add your number" },
                { icon: MapPin, label: "Studio", value: "New York, NY" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="h-12 w-12 rounded-2xl bg-brand-blush flex items-center justify-center text-brand-rose shrink-0"><c.icon size={20} /></span>
                  <div><p className="text-xs uppercase tracking-[0.2em] text-brand-ink/50">{c.label}</p><p className="text-brand-ink font-light mt-1">{c.value}</p></div>
                </div>
              ))}
            </div>
            <div className="mt-10 bg-brand-ink text-brand-cream rounded-3xl p-8">
              <Calendar className="text-brand-gold" size={24} />
              <h3 className="font-serif text-2xl mt-4">Prefer to book instantly?</h3>
              <p className="text-brand-cream/70 font-light mt-2 text-sm">Reserve luxury services directly through our online booking system.</p>
              <button onClick={openBooking} data-testid="contact-book-btn" className="btn-fill bg-brand-gold text-brand-ink rounded-full px-7 py-3 text-sm font-medium mt-6">Book on Vagaro</button>
            </div>
          </div>

          <motion.form initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }} onSubmit={submit} data-testid="contact-form" className="bg-brand-blush rounded-[2rem] p-8 md:p-11">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm text-brand-ink/70 mb-2 block">Name</label><input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} /></div>
              <div><label className="text-sm text-brand-ink/70 mb-2 block">Email</label><input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div><label className="text-sm text-brand-ink/70 mb-2 block">Phone (optional)</label><input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" className={inputCls} /></div>
              <div><label className="text-sm text-brand-ink/70 mb-2 block">I&apos;m interested in</label>
                <select data-testid="contact-interest" value={form.interest} onChange={set("interest")} className={inputCls}>
                  {INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4"><label className="text-sm text-brand-ink/70 mb-2 block">Message</label><textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us how we can help..." className={`${inputCls} resize-none`} /></div>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-fill bg-brand-ink text-brand-cream rounded-full px-9 py-4 text-sm font-medium mt-6 disabled:opacity-60">{loading ? "Sending..." : "Send Message"}</button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
